import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Download, Check, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { StepGraphic, PreviewErrorIllustration } from './Illustrations';
import { API_BASE_URL } from '../config';

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.35,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

const TemplateSelector = ({ submissionId, userId, onFinished }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // New multi-step upgrade & download flow states
  const [isTemplateConfirmed, setIsTemplateConfirmed] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeProgress, setUpgradeProgress] = useState(0);
  const [upgradeStatusText, setUpgradeStatusText] = useState('');
  const [readyDownloadUrl, setReadyDownloadUrl] = useState(null);

  // Once user clicks upgrade, template selection is locked and cannot be re-selected
  const hasStartedUpgrade = isUpgrading || Boolean(readyDownloadUrl) || upgradeProgress > 0;

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/templates`)
      .then(res => {
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        return res.json();
      })
      .then(data => {
        const tplList = Array.isArray(data) ? data : (data && Array.isArray(data.templates) ? data.templates : []);
        setTemplates(tplList);
        if (tplList.length > 0 && !selectedTemplate) {
          setSelectedTemplate(tplList[0]);
        }
      })
      .catch(err => {
        console.error("Failed to load templates:", err);
        setFetchError("Could not reach template registry. Please refresh the page.");
        setTemplates([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreview = async () => {
    if (!selectedTemplate) return;
    setPreviewLoading(true);
    setFetchError(null);
    try {
      const token = localStorage.getItem('access_token');
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      const res = await fetch(`${API_BASE_URL}/api/preview/${submissionId}?template_id=${selectedTemplate.id}&user_id=${userId}`, {
        headers: { ...authHeaders }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } else {
        const errJson = await res.json().catch(() => ({ detail: "Preview generation failed on server." }));
        const msg = errJson.detail || "Unable to generate preview";
        setFetchError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    } catch (err) {
      console.error(err);
      setFetchError(`Network error during preview: ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleStartUpgrading = async () => {
    if (!selectedTemplate || isUpgrading) return;
    setIsUpgrading(true);
    setUpgradeProgress(10);
    setUpgradeStatusText("Analyzing layout & ATS keyword density...");
    setFetchError(null);
    setReadyDownloadUrl(null);

    let currentPct = 10;
    const timer = setInterval(() => {
      currentPct += Math.floor(Math.random() * 8) + 6;
      if (currentPct >= 92) {
        currentPct = 92;
        setUpgradeStatusText("Compiling print-ready PDF...");
      } else if (currentPct > 65) {
        setUpgradeStatusText("Applying template styling & fonts...");
      } else if (currentPct > 35) {
        setUpgradeStatusText("Aligning fact-locked resume data...");
      }
      setUpgradeProgress(currentPct);
    }, 240);

    try {
      const token = localStorage.getItem('access_token');
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      const res = await fetch(`${API_BASE_URL}/api/generate-resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ 
          user_id: userId, 
          submission_id: String(submissionId),
          template_file: selectedTemplate.fileName 
        })
      });

      const data = await res.json();
      clearInterval(timer);

      if (res.ok && data.status === 'success' && data.download_url) {
        setUpgradeProgress(100);
        setUpgradeStatusText("100% Ready! PDF compiled successfully.");
        setReadyDownloadUrl(`${API_BASE_URL}${data.download_url}`);
        setIsUpgrading(false);
      } else {
        setIsUpgrading(false);
        setUpgradeProgress(0);
        const msg = data.detail || data.message || "Upgrade failed. Please try again.";
        setFetchError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    } catch (err) {
      clearInterval(timer);
      setIsUpgrading(false);
      setUpgradeProgress(0);
      setFetchError(`Upgrade error: ${err.message}`);
    }
  };

  const handleDownloadReadyPdf = () => {
    if (!readyDownloadUrl) return;
    window.location.href = readyDownloadUrl;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (onFinished) {
      setTimeout(onFinished, 1500);
    }
  };

  return (
    <div className="p-3.5 sm:p-6 max-w-5xl mx-auto relative space-y-4 sm:space-y-6">
      {/* Header with Step Indicator */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="border-b border-slate-800 pb-3 sm:pb-4"
      >
        <div className="flex items-center gap-1.5 sm:gap-2 mb-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <Check className="w-3.5 h-3.5" /> Payment Verified
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
            <StepGraphic step={3} className="w-3.5 h-3.5" />
            Step 3 of 4: Template Formatting
          </span>
        </div>

        <h2 className="text-xl sm:text-2xl font-bold text-slate-100 flex items-center gap-2">
          <span>Choose Your Tailored Resume Layout</span>
        </h2>
        <p className="text-xs text-slate-400 mt-1">
          Pick your preferred ATS design layout below. Select your template, start upgrading, and download your final print-ready PDF once 100% complete.
        </p>
      </motion.div>

      {/* Error Banner with Illustration if templates failed */}
      <AnimatePresence>
        {fetchError && (
          <motion.div 
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="p-4 sm:p-6 bg-slate-900/80 border border-rose-500/40 rounded-2xl text-center space-y-3"
          >
            <PreviewErrorIllustration className="w-20 h-20 sm:w-24 sm:h-24 mx-auto" />
            <div className="flex items-center justify-center gap-2 text-rose-400 text-xs sm:text-sm font-semibold">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{fetchError}</span>
            </div>
            <button 
              onClick={() => setFetchError(null)} 
              className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 transition cursor-pointer"
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* 3 Template Cards Grid with Staggered Cascading entrance */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
      >
        {templates.length === 0 && !fetchError && (
          <div className="col-span-3 text-center text-slate-500 py-12 animate-pulse">
            Loading design templates...
          </div>
        )}
        {templates.map(tpl => {
          const isSelected = selectedTemplate?.id === tpl.id;
          return (
            <motion.div 
              key={tpl.id} 
              variants={cardVariants}
              whileHover={!hasStartedUpgrade ? { scale: 1.025, y: -3 } : {}}
              whileTap={!hasStartedUpgrade ? { scale: 0.985 } : {}}
              transition={{ duration: 0.2 }}
              onClick={() => {
                if (hasStartedUpgrade) return; // Prevent re-selecting any template once upgrade is clicked
                if (selectedTemplate?.id !== tpl.id) {
                  setSelectedTemplate(tpl);
                  setIsTemplateConfirmed(false);
                  setReadyDownloadUrl(null);
                  setUpgradeProgress(0);
                  setUpgradeStatusText('');
                }
              }}
              className={`rounded-2xl p-3 transition-all duration-200 relative bg-slate-900/80 border-2 will-change-transform ${
                hasStartedUpgrade && !isSelected
                  ? 'opacity-35 cursor-not-allowed pointer-events-none border-slate-900 grayscale-[40%]'
                  : hasStartedUpgrade && isSelected
                    ? 'border-indigo-500 shadow-xl shadow-indigo-500/25 ring-2 ring-indigo-500/30 cursor-default'
                    : isSelected 
                      ? 'cursor-pointer border-indigo-500 shadow-xl shadow-indigo-500/25 ring-2 ring-indigo-500/30' 
                      : 'cursor-pointer border-slate-800 hover:border-slate-700 hover:shadow-lg hover:shadow-slate-950/50'
              }`}
            >
              {/* Thumbnail Image */}
              <div className="relative overflow-hidden rounded-xl bg-slate-950 aspect-[210/297] flex items-center justify-center border border-slate-800 shadow-md group">
                <img 
                  src={`${tpl.thumbnailUrl}${tpl.thumbnailUrl?.includes('?') ? '&' : '?'}v=2`} 
                  alt={tpl.name} 
                  className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x424?text=Template+Preview'; }} 
                />
                {isSelected && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className="absolute top-3 right-3 bg-indigo-600 text-white rounded-full p-1.5 shadow-lg border border-white/20"
                  >
                    <Check className="w-4 h-4 stroke-[3]" />
                  </motion.div>
                )}
              </div>

              {/* Template Title */}
              <div className="mt-3 text-center">
                <p className={`font-semibold text-sm ${isSelected ? 'text-indigo-300' : 'text-slate-300'}`}>
                  {tpl.name}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {isSelected 
                    ? (hasStartedUpgrade ? '🔒 Locked Template' : isTemplateConfirmed ? '✓ Confirmed Format' : 'Active Template') 
                    : (hasStartedUpgrade ? 'Selection disabled' : 'Click to select')}
                </p>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Action Controls & Multi-Step Upgrade Flow */}
      {selectedTemplate && (
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4 pt-2"
        >
          {/* Main Preview Action Button */}
          <motion.button 
            type="button"
            id="view-preview-btn"
            onClick={handlePreview} 
            disabled={previewLoading || isUpgrading}
            whileHover={!previewLoading && !isUpgrading ? { scale: 1.02 } : {}}
            whileTap={!previewLoading && !isUpgrading ? { scale: 0.98 } : {}}
            transition={{ duration: 0.2 }}
            className="w-full py-3.5 px-6 bg-slate-800 hover:bg-slate-700 border border-slate-700 disabled:opacity-60 text-white rounded-xl font-bold text-sm sm:text-base transition duration-200 flex items-center justify-center gap-3 shadow-md cursor-pointer will-change-transform"
          >
            {previewLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                <span>Rendering Live Preview in Selected Format...</span>
              </>
            ) : (
              <>
                <Eye className="w-5 h-5 text-indigo-400" />
                <span>View Updated Details in Selected Format</span>
              </>
            )}
          </motion.button>

          {/* Secondary Controls Bar: Select -> Upgrading -> Progress Bar (100%) -> Download PDF */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3.5 sm:p-5 space-y-3.5 sm:space-y-4 shadow-xl">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse"></span>
                <span className="text-xs text-slate-300">
                  Target Template: <strong className="text-white font-semibold">{selectedTemplate.name}</strong>
                </span>
                {readyDownloadUrl ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <Check className="w-3 h-3 stroke-[3]" /> 100% Ready
                  </span>
                ) : isTemplateConfirmed ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <Check className="w-3 h-3 stroke-[3]" /> Selected
                  </span>
                ) : null}
              </div>

              {/* Action Buttons: Selected -> Upgrading -> Download PDF */}
              <div className="flex items-center gap-2 w-full sm:w-auto justify-stretch sm:justify-end flex-wrap sm:flex-nowrap">
                {/* 1. "Select" / "Selected" button (Visible when not upgrading and not yet at 100%) */}
                {!readyDownloadUrl && !isUpgrading && (
                  <motion.button 
                    type="button"
                    disabled={hasStartedUpgrade}
                    whileHover={!hasStartedUpgrade ? { scale: 1.03 } : {}}
                    whileTap={!hasStartedUpgrade ? { scale: 0.97 } : {}}
                    transition={{ duration: 0.2 }}
                    onClick={() => {
                      if (!hasStartedUpgrade) setIsTemplateConfirmed(true);
                    }}
                    className={`flex-1 sm:flex-none justify-center px-4 py-2 rounded-xl font-bold text-xs transition flex items-center gap-1.5 ${
                      hasStartedUpgrade ? 'cursor-default opacity-90' : 'cursor-pointer'
                    } ${
                      isTemplateConfirmed
                        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 shadow-sm shadow-emerald-500/10'
                        : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700'
                    }`}
                  >
                    <Check className={`w-3.5 h-3.5 ${isTemplateConfirmed ? 'text-emerald-400 stroke-[3]' : 'opacity-50'}`} />
                    <span>{isTemplateConfirmed ? 'Selected' : 'Select'}</span>
                  </motion.button>
                )}

                {/* 2. "Upgrade" -> changes to "Upgrading" on click */}
                {isTemplateConfirmed && !readyDownloadUrl && (
                  <motion.button 
                    type="button"
                    disabled={isUpgrading}
                    whileHover={!isUpgrading ? { scale: 1.03, boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.5)' } : {}}
                    whileTap={!isUpgrading ? { scale: 0.97 } : {}}
                    transition={{ duration: 0.2 }}
                    onClick={handleStartUpgrading}
                    className="flex-1 sm:flex-none justify-center px-5 py-2 rounded-xl font-bold text-xs bg-indigo-600 hover:bg-indigo-500 disabled:opacity-85 text-white transition shadow-md shadow-indigo-600/30 flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed"
                  >
                    {isUpgrading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                        <span>Upgrading</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5 text-indigo-200" />
                        <span>Upgrade</span>
                      </>
                    )}
                  </motion.button>
                )}

                {/* 3. "Download PDF" button (Shown ONLY after progress reaches 100%) */}
                {readyDownloadUrl && (
                  <motion.button 
                    type="button"
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileHover={{ scale: 1.03, boxShadow: '0 10px 25px -4px rgba(16, 185, 129, 0.45)' }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.2 }}
                    onClick={handleDownloadReadyPdf}
                    className="w-full sm:w-auto justify-center px-6 py-2.5 rounded-xl font-bold text-xs bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white transition shadow-lg shadow-emerald-600/30 flex items-center gap-2 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </motion.button>
                )}
              </div>
            </div>

            {/* Progress Bar (Visible while upgrading OR when reached 100%) */}
            {(isUpgrading || upgradeProgress > 0) && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.25 }}
                className="space-y-2 pt-2 border-t border-slate-800/80"
              >
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-300 font-medium flex items-center gap-2">
                    {upgradeProgress < 100 ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                    ) : (
                      <Check className="w-3.5 h-3.5 text-emerald-400 stroke-[3]" />
                    )}
                    <span>{upgradeStatusText}</span>
                  </span>
                  <span className={`font-mono font-bold ${upgradeProgress === 100 ? 'text-emerald-400' : 'text-indigo-400'}`}>
                    {upgradeProgress}%
                  </span>
                </div>
                <div className="w-full bg-slate-950 rounded-full h-2.5 overflow-hidden border border-slate-800 p-0.5">
                  <div 
                    className={`h-full rounded-full transition-all duration-300 ${
                      upgradeProgress === 100 
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-400 shadow-md shadow-emerald-500/40' 
                        : 'bg-gradient-to-r from-indigo-500 to-amber-400 shadow-md shadow-indigo-500/40'
                    }`}
                    style={{ width: `${upgradeProgress}%` }}
                  />
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      {/* PDF Preview Modal with Selected -> Upgrading -> Download PDF flow */}
      <AnimatePresence>
        {previewUrl && (
          <motion.div 
            key="preview-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex flex-col items-center justify-center p-2 sm:p-4"
          >
            <motion.div 
              key="preview-modal-dialog"
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 20 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-4xl bg-slate-900 rounded-2xl overflow-hidden flex flex-col h-[94vh] sm:h-[90vh] border border-slate-700 shadow-2xl will-change-transform"
            >
              {/* Modal Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 sm:p-4 bg-slate-950/80 border-b border-slate-800 gap-2.5 sm:gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <h3 className="font-bold text-xs sm:text-sm text-slate-100">
                    Preview: {selectedTemplate?.name}
                  </h3>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap w-full sm:w-auto justify-between sm:justify-end">
                  {/* Upgrading Status Pill */}
                  {isUpgrading && (
                    <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-lg text-xs">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                      <span className="text-slate-300">Upgrading...</span>
                      <span className="font-mono text-indigo-400 font-bold">{upgradeProgress}%</span>
                    </div>
                  )}

                  {/* 1. "Select" / "Selected" in Preview Header */}
                  {!readyDownloadUrl && !isUpgrading && (
                    <motion.button
                      disabled={hasStartedUpgrade}
                      whileHover={!hasStartedUpgrade ? { scale: 1.03 } : {}}
                      whileTap={!hasStartedUpgrade ? { scale: 0.97 } : {}}
                      onClick={() => {
                        if (!hasStartedUpgrade) setIsTemplateConfirmed(true);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${
                        hasStartedUpgrade ? 'cursor-default opacity-90' : 'cursor-pointer'
                      } ${
                        isTemplateConfirmed
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                          : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700'
                      }`}
                    >
                      <Check className={`w-3 h-3 ${isTemplateConfirmed ? 'text-emerald-400 stroke-[3]' : 'opacity-50'}`} />
                      <span>{isTemplateConfirmed ? 'Selected' : 'Select'}</span>
                    </motion.button>
                  )}

                  {/* 2. "Upgrade" -> changes to "Upgrading" in Preview Header */}
                  {isTemplateConfirmed && !readyDownloadUrl && (
                    <motion.button
                      disabled={isUpgrading}
                      whileHover={!isUpgrading ? { scale: 1.03 } : {}}
                      whileTap={!isUpgrading ? { scale: 0.97 } : {}}
                      onClick={handleStartUpgrading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 disabled:opacity-85 text-white transition shadow-sm cursor-pointer disabled:cursor-not-allowed"
                    >
                      {isUpgrading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                          <span>Upgrading</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 text-indigo-200" />
                          <span>Upgrade</span>
                        </>
                      )}
                    </motion.button>
                  )}

                  {/* 3. "Download PDF" in Preview Header (Shown ONLY at 100%) */}
                  {readyDownloadUrl && (
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleDownloadReadyPdf}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition shadow-md shadow-emerald-600/30 cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download PDF</span>
                    </motion.button>
                  )}

                  {/* Close Modal Button */}
                  <motion.button 
                    onClick={() => {
                      URL.revokeObjectURL(previewUrl);
                      setPreviewUrl(null);
                    }} 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.2 }}
                    className="text-slate-400 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-800 transition cursor-pointer"
                  >
                    ✕ Close
                  </motion.button>
                </div>
              </div>

              {/* Embedded PDF iframe */}
              <div className="w-full flex-grow bg-slate-950 p-2 overflow-auto">
                <iframe 
                  src={`${previewUrl}#toolbar=0`} 
                  className="w-full h-full rounded-lg border border-slate-800 bg-white" 
                  title="Resume Preview"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TemplateSelector;
